import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { useFetcher } from "~/hooks/useFetcher";
import { isApiError } from "~/utils/apiClient";

interface CohortCurrentState {
  updated_at: string | null;
}

interface CohortServerState {
  isDeleted: boolean;
  updatedAt: string | null;
}

// cohortId → server state. Missing key means fetch not yet complete.
const CohortStalenessContext = createContext<ReadonlyMap<number, CohortServerState>>(new Map());

interface CohortFetcherProps {
  cohortId: number;
  onResult: (cohortId: number, state: CohortServerState) => void;
}

function CohortFetcher({ cohortId, onResult }: CohortFetcherProps) {
  const { data, error } = useFetcher<CohortCurrentState>(
    `/api/cohort-explorer/v1/cohorts/${cohortId}`
  );
  useEffect(() => {
    if (data !== undefined) {
      onResult(cohortId, { isDeleted: false, updatedAt: data.updated_at ?? null });
    } else if (isApiError(error) && error.status === 404) {
      onResult(cohortId, { isDeleted: true, updatedAt: null });
    }
  }, [cohortId, data, error, onResult]);
  return null;
}

interface CohortStalenessProviderProps {
  cohortIds: number[];
  children: ReactNode;
}

export function CohortStalenessProvider({ cohortIds, children }: CohortStalenessProviderProps) {
  const [serverStates, setServerStates] = useState<Map<number, CohortServerState>>(new Map());

  const handleResult = useCallback((cohortId: number, state: CohortServerState) => {
    setServerStates((prev) => {
      const existing = prev.get(cohortId);
      if (existing?.isDeleted === state.isDeleted && existing?.updatedAt === state.updatedAt) {
        return prev;
      }
      return new Map(prev).set(cohortId, state);
    });
  }, []);

  return (
    <CohortStalenessContext.Provider value={serverStates}>
      {cohortIds.map((id) => (
        <CohortFetcher key={id} cohortId={id} onResult={handleResult} />
      ))}
      {children}
    </CohortStalenessContext.Provider>
  );
}

interface CohortCardState {
  isStale: boolean;
  isDeleted: boolean;
}

export function useCohortServerState(
  cohortId: number,
  cardUpdatedAt: string | null | undefined
): CohortCardState {
  const serverStates = useContext(CohortStalenessContext);
  const state = serverStates.get(cohortId);

  if (!state) return { isStale: false, isDeleted: false };
  if (state.isDeleted) return { isStale: false, isDeleted: true };

  const isStale =
    !!cardUpdatedAt && !!state.updatedAt && new Date(state.updatedAt) > new Date(cardUpdatedAt);

  return { isStale, isDeleted: false };
}

// Kept for callers that only care about staleness.
export function useCohortStale(
  cohortId: number,
  cardUpdatedAt: string | null | undefined
): boolean {
  return useCohortServerState(cohortId, cardUpdatedAt).isStale;
}
